import React, { useState, useEffect } from 'react';
import {
  Search, Plus, Send, RefreshCw, Clock, CheckCircle, PauseCircle,
  XCircle, ChevronRight, Copy, ExternalLink, Repeat, FileText, Paperclip,
  Pen, Eye, Bell, ShieldCheck
} from 'lucide-react';
import {
  getEngagements, createEngagement, updateEngagement, sendEngagementLink,
  sendEngagementForSignature, nudgeEngagementSignature
} from '../../services/adminDealFlowApi';
import api from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/new-components/ui/card';
import { Button } from '../../components/new-components/ui/button';
import { Badge } from '../../components/new-components/ui/badge';
import { Input } from '../../components/new-components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../../components/new-components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../../components/new-components/ui/select';

const formatCurrency = (cents: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents / 100);

const statusIcon = (s: string) => {
  switch (s) {
    case 'active': return <CheckCircle className="h-4 w-4 text-green-600" />;
    case 'paused': return <PauseCircle className="h-4 w-4 text-amber-500" />;
    case 'completed': return <CheckCircle className="h-4 w-4 text-blue-600" />;
    case 'cancelled': return <XCircle className="h-4 w-4 text-red-500" />;
    default: return <Clock className="h-4 w-4 text-gray-400" />;
  }
};

const statusColor = (s: string): 'default' | 'secondary' | 'destructive' | 'outline' => {
  switch (s) {
    case 'active': return 'default';
    case 'completed': return 'secondary';
    case 'cancelled': return 'destructive';
    default: return 'outline';
  }
};

// Unwrap API response: handles both axios-wrapped {data: {success, data, ...}} and direct body {success, data, ...}
const unwrap = (res: any) => {
  // If res has .status (axios response), the body is res.data
  // If res has .success (already the body), use it directly
  const body = res?.data?.success !== undefined ? res.data : (res?.success !== undefined ? res : res?.data || res);
  return body;
};

const Engagements: React.FC = () => {
  const [engagements, setEngagements] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 25;

  // Create dialog
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newEng, setNewEng] = useState({
    title: '',
    description: '',
    contact_email: '',
    contact_name: '',
    type: 'one_time' as string,
    frequency: '' as string,
    amount: '',
    methods_accepted: ['ach', 'usdc'] as string[],
    recurring_count: '',
    next_due_date: '',
    increase_account_id: '' as string,
  });

  // Increase accounts for receiving account selector
  const [increaseAccounts, setIncreaseAccounts] = useState<any[]>([]);

  // Document attachment state
  const [availableDocs, setAvailableDocs] = useState<any[]>([]);
  const [selectedDocIds, setSelectedDocIds] = useState<string[]>([]);
  const [requiresSignature, setRequiresSignature] = useState(false);
  const [loadingDocs, setLoadingDocs] = useState(false);

  // Detail dialog
  const [selected, setSelected] = useState<any>(null);
  const [showDetail, setShowDetail] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [sending, setSending] = useState(false);

  // Schedule state
  const [showSchedule, setShowSchedule] = useState<'link' | 'sig' | null>(null);
  const [scheduleDate, setScheduleDate] = useState('');

  const load = async () => {
    try {
      setLoading(true);
      const params: any = { search, page, limit };
      if (statusFilter !== 'all') params.status = statusFilter;
      if (typeFilter !== 'all') params.type = typeFilter;
      const res = await getEngagements(params);
      const body = unwrap(res);
      const items = Array.isArray(body?.data) ? body.data : [];
      setEngagements(items);
      setTotal(body?.pagination?.total || items.length);
    } catch (err) {
      console.error('Failed to load engagements', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [search, statusFilter, typeFilter, page]);

  // Load Increase accounts once on mount for detail view name resolution
  useEffect(() => {
    api.get('/admin/engagements/increase-accounts')
      .then(res => { const body = unwrap(res); setIncreaseAccounts(body.data || []); })
      .catch(() => {});
  }, []);

  // Load available documents + Increase accounts when create dialog opens
  useEffect(() => {
    if (showCreate) {
      setLoadingDocs(true);
      api.get('/admin/documents', { params: { limit: 100 } })
        .then(res => {
          const body = unwrap(res);
          setAvailableDocs(body.documents || []);
        })
        .catch(() => setAvailableDocs([]))
        .finally(() => setLoadingDocs(false));
      api.get('/admin/engagements/increase-accounts')
        .then(res => { const body = unwrap(res); setIncreaseAccounts(body.data || []); })
        .catch(() => setIncreaseAccounts([]));
    }
  }, [showCreate]);

  const handleCreate = async () => {
    if (!newEng.title || !newEng.amount) return;
    try {
      setCreating(true);
      // Use api.post directly to include document_ids and requires_signature
      await api.post('/admin/engagements/engagements', {
        title: newEng.title,
        description: newEng.description || undefined,
        contact_email: newEng.contact_email || undefined,
        contact_name: newEng.contact_name || undefined,
        type: newEng.type,
        frequency: newEng.type === 'recurring' ? newEng.frequency : undefined,
        amount_cents: Math.round(parseFloat(newEng.amount) * 100),
        methods_accepted: newEng.methods_accepted,
        recurring_count: newEng.recurring_count ? parseInt(newEng.recurring_count) : undefined,
        next_due_date: newEng.next_due_date || undefined,
        document_ids: selectedDocIds.length > 0 ? selectedDocIds : undefined,
        requires_signature: requiresSignature || undefined,
        increase_account_id: newEng.increase_account_id || undefined,
      });
      setShowCreate(false);
      setNewEng({ title: '', description: '', contact_email: '', contact_name: '', type: 'one_time', frequency: '', amount: '', methods_accepted: ['ach', 'usdc'], recurring_count: '', next_due_date: '', increase_account_id: '' });
      setSelectedDocIds([]);
      setRequiresSignature(false);
      load();
    } catch (err) {
      console.error('Failed to create engagement', err);
    } finally {
      setCreating(false);
    }
  };

  const handleSendLink = async (eng: any) => {
    try {
      setSending(true);
      await sendEngagementLink(eng.id);
      load();
    } catch (err) {
      console.error('Failed to send link', err);
    } finally {
      setSending(false);
    }
  };

  const handleScheduleSend = async () => {
    if (!selected || !scheduleDate) return;
    try {
      setSending(true);
      if (showSchedule === 'link') {
        await sendEngagementLink(selected.id, { scheduled_at: new Date(scheduleDate).toISOString() });
      } else if (showSchedule === 'sig') {
        await sendEngagementForSignature(selected.id, { scheduled_at: new Date(scheduleDate).toISOString() });
      }
      setShowSchedule(null);
      setScheduleDate('');
      load();
    } catch (err) {
      console.error('Failed to schedule', err);
    } finally {
      setSending(false);
    }
  };

  const handleStatusChange = async (eng: any, newStatus: string) => {
    try {
      await updateEngagement(eng.id, { status: newStatus });
      load();
      if (selected?.id === eng.id) setSelected({ ...selected, status: newStatus });
    } catch (err) {
      console.error('Failed to update status', err);
    }
  };

  const copyPaymentLink = (eng: any) => {
    const link = `${window.location.origin}/pay/${eng.link_token}`;
    navigator.clipboard.writeText(link);
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  };

  const totalPages = Math.ceil(total / limit);

  const methodsFor = (eng: any): string[] => {
    if (Array.isArray(eng.methods_accepted)) return eng.methods_accepted;
    try { return JSON.parse(eng.methods_accepted || '[]'); } catch { return []; }
  };

  const openEngagement = async (eng: any) => {
    setSelected(eng);
    setShowDetail(true);
    try {
      const res = await api.get(`/admin/engagements/engagements/${eng.id}`);
      const body = unwrap(res);
      setSelected({ ...eng, ...(body.data || body) });
    } catch (err) { console.error('Failed to load detail', err); }
  };

  return (
    <div className="space-y-5 sm:space-y-6 min-w-0">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
        <div className="min-w-0">
          <h1 className="text-xl sm:text-2xl font-display font-semibold text-[#242424]">Engagements</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {total} engagements — Send payment links for one-time or recurring ACH & USDC payments
          </p>
        </div>
        <Button onClick={() => setShowCreate(true)} className="w-full sm:w-auto gap-2">
          <Plus className="h-4 w-4" /> New Engagement
        </Button>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 sm:grid-cols-[minmax(0,1fr)_auto_auto_auto] gap-3 sm:items-center">
        <div className="relative min-w-0 sm:max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search engagements..."
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
            className="pl-9"
          />
        </div>
        <Select value={statusFilter} onValueChange={v => { setStatusFilter(v); setPage(1); }}>
          <SelectTrigger className="w-full sm:w-36">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="paused">Paused</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
        <Select value={typeFilter} onValueChange={v => { setTypeFilter(v); setPage(1); }}>
          <SelectTrigger className="w-full sm:w-36">
            <SelectValue placeholder="Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="one_time">One-Time</SelectItem>
            <SelectItem value="recurring">Recurring</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" onClick={load} className="w-full sm:w-auto gap-1">
          <RefreshCw className="h-3.5 w-3.5" /> Refresh
        </Button>
      </div>

      {/* Table */}
      <Card className="border-border">
        <CardContent className="p-0">
          <div className="md:hidden divide-y divide-border">
            {loading ? (
              <div className="p-10 text-center text-muted-foreground">
                <div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full mx-auto" />
              </div>
            ) : (engagements || []).length === 0 ? (
              <div className="p-8 text-center text-sm text-muted-foreground">
                No engagements found. Create one to get started.
              </div>
            ) : (engagements || []).map(eng => (
              <button
                key={eng.id}
                onClick={() => openEngagement(eng)}
                className="w-full p-4 text-left hover:bg-muted/40 transition-colors"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-[#242424] truncate">{eng.title}</p>
                    <p className="text-xs text-muted-foreground font-mono break-all">{eng.engagement_id}</p>
                  </div>
                  <p className="text-sm font-semibold text-[#242424] shrink-0">{formatCurrency(eng.amount_cents)}</p>
                </div>
                <div className="mt-3 grid grid-cols-2 gap-3 text-xs">
                  <div className="min-w-0">
                    <p className="text-muted-foreground">Recipient</p>
                    <p className="text-[#242424] truncate">{eng.contact_name || '—'}</p>
                    <p className="text-muted-foreground truncate">{eng.contact_email || 'No email'}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Collected</p>
                    <p className="font-medium text-green-700">{formatCurrency(eng.total_settled_cents || 0)}</p>
                    {(eng.total_pending_cents || 0) > 0 && <p className="text-amber-600">{formatCurrency(eng.total_pending_cents)} pending</p>}
                  </div>
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-1.5">
                  {statusIcon(eng.status)}
                  <Badge variant={statusColor(eng.status)} className="text-xs capitalize">{eng.status}</Badge>
                  {methodsFor(eng).map((m: string) => <Badge key={m} variant="outline" className="text-xs uppercase">{m}</Badge>)}
                  {Number(eng.signature_total) > 0 && (
                    <Badge variant="outline" className="text-xs">Signature {eng.signature_signed}/{eng.signature_total}</Badge>
                  )}
                </div>
              </button>
            ))}
          </div>

          <div className="hidden md:block overflow-x-auto">
            <table className="w-full min-w-[980px]">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left p-4 text-xs text-muted-foreground font-medium">Engagement</th>
                  <th className="text-left p-4 text-xs text-muted-foreground font-medium">Recipient</th>
                  <th className="text-left p-4 text-xs text-muted-foreground font-medium">Type</th>
                  <th className="text-right p-4 text-xs text-muted-foreground font-medium">Amount</th>
                  <th className="text-left p-4 text-xs text-muted-foreground font-medium">Status</th>
                  <th className="text-right p-4 text-xs text-muted-foreground font-medium">Collected</th>
                  <th className="text-center p-4 text-xs text-muted-foreground font-medium">E-Signature</th>
                  <th className="text-left p-4 text-xs text-muted-foreground font-medium">Methods</th>
                  <th className="p-4"></th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={9} className="p-12 text-center text-muted-foreground">
                      <div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full mx-auto" />
                    </td>
                  </tr>
                ) : (engagements || []).length === 0 ? (
                  <tr>
                    <td colSpan={9} className="p-12 text-center text-muted-foreground">
                      No engagements found. Create one to get started.
                    </td>
                  </tr>
                ) : (engagements || []).map(eng => (
                  <tr
                    key={eng.id}
                    className="border-b border-border hover:bg-muted/50 cursor-pointer"
                    onClick={() => openEngagement(eng)}
                  >
                  <td className="p-4">
                    <div>
                      <p className="text-sm font-medium text-[#242424]">{eng.title}</p>
                      <p className="text-xs text-muted-foreground font-mono">{eng.engagement_id}</p>
                    </div>
                  </td>
                  <td className="p-4">
                    <div>
                      <p className="text-sm text-[#242424]">{eng.contact_name || '—'}</p>
                      <p className="text-xs text-muted-foreground">{eng.contact_email || 'No email'}</p>
                    </div>
                  </td>
                  <td className="p-4">
                    <div className="flex items-center gap-1.5">
                      {eng.type === 'recurring' ? (
                        <Repeat className="h-3.5 w-3.5 text-blue-600" />
                      ) : (
                        <Clock className="h-3.5 w-3.5 text-gray-400" />
                      )}
                      <span className="text-sm text-[#242424] capitalize">
                        {eng.type === 'one_time' ? 'One-time' : 'Recurring'}
                      </span>
                    </div>
                    {eng.frequency && (
                      <p className="text-xs text-muted-foreground capitalize mt-0.5">{eng.frequency}</p>
                    )}
                  </td>
                  <td className="p-4 text-right">
                    <p className="text-sm font-medium text-[#242424]">{formatCurrency(eng.amount_cents)}</p>
                  </td>
                  <td className="p-4">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {statusIcon(eng.status)}
                      <Badge variant={statusColor(eng.status)} className="text-xs capitalize">
                        {eng.status}
                      </Badge>
                      {eng.past_due_notified_at && (
                        <Badge variant="destructive" className="text-xs">Past Due</Badge>
                      )}
                      {eng.auto_charge && (
                        <Badge variant="default" className="bg-green-100 text-green-800 text-xs">Auto</Badge>
                      )}
                    </div>
                  </td>
                  <td className="p-4 text-right">
                    <p className="text-sm font-medium text-green-700">
                      {formatCurrency(eng.total_settled_cents || 0)}
                    </p>
                    {(eng.total_pending_cents || 0) > 0 && (
                      <p className="text-xs text-amber-600">
                        {formatCurrency(eng.total_pending_cents)} pending
                      </p>
                    )}
                    <p className="text-xs text-muted-foreground">
                      {eng.transaction_count || 0} txn{(eng.transaction_count || 0) !== 1 ? 's' : ''}
                    </p>
                  </td>
                  <td className="p-4 text-center">
                    {Number(eng.signature_total) > 0 ? (
                      <div className="flex flex-col items-center gap-0.5">
                        {Number(eng.signature_signed) === Number(eng.signature_total) ? (
                          <Badge variant="default" className="text-xs bg-green-600 gap-1">
                            <ShieldCheck className="h-3 w-3" /> Signed
                          </Badge>
                        ) : Number(eng.signature_viewed) > 0 ? (
                          <Badge variant="outline" className="text-xs text-amber-600 border-amber-300 gap-1">
                            <Eye className="h-3 w-3" /> Viewed
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-xs text-blue-600 border-blue-300 gap-1">
                            <Pen className="h-3 w-3" /> Sent
                          </Badge>
                        )}
                        <span className="text-[10px] text-muted-foreground">
                          {eng.signature_signed}/{eng.signature_total}
                        </span>
                      </div>
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="p-4">
                    <div className="flex gap-1">
                      {methodsFor(eng).map((m: string) => (
                        <Badge key={m} variant="outline" className="text-xs uppercase">{m}</Badge>
                      ))}
                    </div>
                  </td>
                  <td className="p-4 text-right">
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  </td>
                </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <p className="text-sm text-muted-foreground">
            Page {page} of {totalPages} ({total} total)
          </p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
              Previous
            </Button>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>
              Next
            </Button>
          </div>
        </div>
      )}

      {/* ===== Create Dialog ===== */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Engagement</DialogTitle>
          </DialogHeader>
          <div className="space-y-5 py-4">
            <div>
              <label className="text-sm font-medium text-[#242424]">Title *</label>
              <Input value={newEng.title} onChange={e => setNewEng({ ...newEng, title: e.target.value })} placeholder="e.g. Q1 Capital Call" className="mt-1" />
            </div>
            <div>
              <label className="text-sm font-medium text-[#242424]">Description</label>
              <Input value={newEng.description} onChange={e => setNewEng({ ...newEng, description: e.target.value })} placeholder="Optional notes" className="mt-1" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium text-[#242424]">Recipient Name</label>
                <Input value={newEng.contact_name} onChange={e => setNewEng({ ...newEng, contact_name: e.target.value })} placeholder="John Doe" className="mt-1" />
              </div>
              <div>
                <label className="text-sm font-medium text-[#242424]">Recipient Email</label>
                <Input value={newEng.contact_email} onChange={e => setNewEng({ ...newEng, contact_email: e.target.value })} placeholder="john@example.com" className="mt-1" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium text-[#242424]">Amount (USD) *</label>
                <Input type="number" value={newEng.amount} onChange={e => setNewEng({ ...newEng, amount: e.target.value })} placeholder="1000.00" className="mt-1" />
              </div>
              <div>
                <label className="text-sm font-medium text-[#242424]">Type</label>
                <Select value={newEng.type} onValueChange={v => setNewEng({ ...newEng, type: v })}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="one_time">One-Time</SelectItem>
                    <SelectItem value="recurring">Recurring</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {newEng.type === 'recurring' && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium text-[#242424]">Frequency</label>
                  <Select value={newEng.frequency} onValueChange={v => setNewEng({ ...newEng, frequency: v })}>
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="weekly">Weekly</SelectItem>
                      <SelectItem value="biweekly">Bi-weekly</SelectItem>
                      <SelectItem value="monthly">Monthly</SelectItem>
                      <SelectItem value="quarterly">Quarterly</SelectItem>
                      <SelectItem value="annually">Annually</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium text-[#242424]"># of Payments</label>
                  <Input type="number" value={newEng.recurring_count} onChange={e => setNewEng({ ...newEng, recurring_count: e.target.value })} placeholder="∞" className="mt-1" />
                </div>
                <div className="col-span-2">
                  <label className="text-sm font-medium text-[#242424]">First Due Date</label>
                  <Input type="date" value={newEng.next_due_date} onChange={e => setNewEng({ ...newEng, next_due_date: e.target.value })} className="mt-1" />
                  <p className="text-xs text-muted-foreground mt-1">Payment link will auto-send on this date and recur per frequency</p>
                </div>
              </div>
            )}
            <div>
              <label className="text-sm font-medium text-[#242424]">Payment Methods</label>
              <div className="flex gap-3 mt-1">
                {['ach', 'usdc'].map(m => (
                  <label key={m} className="flex items-center gap-2 text-sm text-[#242424]">
                    <input
                      type="checkbox"
                      checked={newEng.methods_accepted.includes(m)}
                      onChange={e => {
                        const methods = e.target.checked
                          ? [...newEng.methods_accepted, m]
                          : newEng.methods_accepted.filter(x => x !== m);
                        setNewEng({ ...newEng, methods_accepted: methods });
                      }}
                      className="rounded border-gray-300"
                    />
                    {m.toUpperCase()}
                  </label>
                ))}
              </div>
            </div>

            {/* Receiving Increase Account */}
            {increaseAccounts.length > 0 && (
              <div>
                <label className="text-sm font-medium text-[#242424]">Receiving Account</label>
                <p className="text-xs text-muted-foreground mt-0.5 mb-1">Which Increase account should receive this payment?</p>
                <Select
                  value={newEng.increase_account_id || '__default__'}
                  onValueChange={v => setNewEng({ ...newEng, increase_account_id: v === '__default__' ? '' : v })}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Platform default" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__default__">Platform default</SelectItem>
                    {increaseAccounts.map((acct: any) => (
                      <SelectItem key={acct.id} value={acct.id}>
                        {acct.name}{acct.is_default ? ' (default)' : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Document Attachments */}
            <div>
              <label className="text-sm font-medium text-[#242424] flex items-center gap-1">
                <Paperclip className="h-3.5 w-3.5" /> Attach Documents
              </label>
              <p className="text-xs text-muted-foreground mt-0.5 mb-2">
                Select documents to attach to the engagement email. Upload new ones in the Documents tab first.
              </p>
              {loadingDocs ? (
                <p className="text-xs text-muted-foreground">Loading documents...</p>
              ) : availableDocs.length === 0 ? (
                <p className="text-xs text-muted-foreground italic">No documents available. Upload some in the Documents tab.</p>
              ) : (
                <div className="border rounded-lg max-h-48 overflow-y-auto divide-y divide-border">
                  {availableDocs.map(doc => (
                    <label key={doc.id} className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-muted/50 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedDocIds.includes(doc.id)}
                        onChange={e => {
                          if (e.target.checked) setSelectedDocIds(prev => [...prev, doc.id]);
                          else setSelectedDocIds(prev => prev.filter(x => x !== doc.id));
                        }}
                        className="rounded border-gray-300"
                      />
                      <FileText className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                      <span className="truncate flex-1">{doc.title}</span>
                      <Badge variant="outline" className="text-xs">{doc.folder}</Badge>
                    </label>
                  ))}
                </div>
              )}
              {selectedDocIds.length > 0 && (
                <label className="flex items-center gap-2 mt-2 text-sm text-[#242424]">
                  <input
                    type="checkbox"
                    checked={requiresSignature}
                    onChange={e => setRequiresSignature(e.target.checked)}
                    className="rounded border-gray-300"
                  />
                  <span>Require signature — payer must sign documents before payment</span>
                </label>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button onClick={handleCreate} disabled={creating || !newEng.title || !newEng.amount} className="gap-2">
              {creating ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
              Create & Activate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===== Detail Dialog ===== */}
      <Dialog open={showDetail} onOpenChange={setShowDetail}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  {statusIcon(selected.status)}
                  {selected.title}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-5 py-4">
                {/* ID & Amount */}
                <div className="flex items-center justify-between">
                  <span className="text-xs font-mono text-muted-foreground">{selected.engagement_id}</span>
                  <span className="text-lg font-semibold text-[#242424]">{formatCurrency(selected.amount_cents)}</span>
                </div>

                {/* Meta grid */}
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-muted-foreground text-xs">Type</p>
                    <p className="text-[#242424] capitalize">{selected.type === 'one_time' ? 'One-time' : `Recurring (${selected.frequency || '—'})`}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground text-xs">Status</p>
                    <Badge variant={statusColor(selected.status)} className="capitalize">{selected.status}</Badge>
                  </div>
                  <div>
                    <p className="text-muted-foreground text-xs">Recipient</p>
                    <p className="text-[#242424]">{selected.contact_name || '—'}</p>
                    <p className="text-xs text-muted-foreground">{selected.contact_email || 'No email'}</p>
                  </div>
                  {selected.increase_account_id && (
                    <div>
                      <p className="text-muted-foreground text-xs">Receiving Account</p>
                      <p className="text-[#242424] text-sm">
                        {increaseAccounts.find((a: any) => a.id === selected.increase_account_id)?.name || 'Custom account'}
                      </p>
                    </div>
                  )}
                  <div>
                    <p className="text-muted-foreground text-xs">Created</p>
                    <p className="text-[#242424]">{new Date(selected.created_at).toLocaleDateString()}</p>
                  </div>
                  {selected.type === 'recurring' && (
                    <>
                      <div>
                        <p className="text-muted-foreground text-xs">Progress</p>
                        <p className="text-[#242424]">{selected.recurring_completed || 0} / {selected.recurring_count || '∞'} payments</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground text-xs">Next Due</p>
                        <p className="text-[#242424]">{selected.next_due_date ? new Date(selected.next_due_date).toLocaleDateString() : '—'}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground text-xs">Auto-Charge</p>
                        <p className="text-[#242424]">
                          {selected.auto_charge
                            ? <Badge variant="default" className="bg-green-100 text-green-800 text-xs">Active — ACH ****{(selected.stored_payment_method as any)?.account_last4 || '????'}</Badge>
                            : <span className="text-muted-foreground">Off</span>}
                        </p>
                      </div>
                    </>
                  )}
                </div>

                {/* Collected summary */}
                <Card className="border-border bg-gray-50">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-muted-foreground">Total Collected</p>
                        <p className="text-lg font-semibold text-green-700">{formatCurrency(selected.total_settled_cents || 0)}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-muted-foreground">Pending</p>
                        <p className="text-lg font-semibold text-amber-600">{formatCurrency(selected.total_pending_cents || 0)}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-muted-foreground">Transactions</p>
                        <p className="text-lg font-semibold text-[#242424]">{selected.transaction_count || 0}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* E-Signature Status */}
                {(selected.signatures?.length > 0 || Number(selected.signature_total) > 0) && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">E-Signatures</p>
                      {selected.signatures?.some((s: any) => ['sent','pending','viewed'].includes(s.status)) && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="gap-1 h-7 text-xs"
                          onClick={async () => {
                            try {
                              await nudgeEngagementSignature(selected.id);
                              // Refresh detail
                              const res = await api.get(`/admin/engagements/engagements/${selected.id}`);
                              const body = unwrap(res);
                              setSelected(body.data || body);
                            } catch (err) { console.error('Nudge failed', err); }
                          }}
                        >
                          <Bell className="h-3 w-3" /> Nudge
                        </Button>
                      )}
                    </div>
                    <div className="space-y-2">
                      {(selected.signatures || []).map((sig: any) => (
                        <div key={sig.id} className="flex items-center justify-between p-3 rounded-lg border border-border bg-white">
                          <div className="flex items-center gap-3 min-w-0">
                            <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-[#242424] truncate">{sig.template_name || 'Document'}</p>
                              <p className="text-xs text-muted-foreground">{sig.signer_email}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            {sig.status === 'signed' ? (
                              <Badge variant="default" className="bg-green-600 gap-1 text-xs">
                                <ShieldCheck className="h-3 w-3" /> Signed
                              </Badge>
                            ) : sig.status === 'viewed' ? (
                              <Badge variant="outline" className="text-amber-600 border-amber-300 gap-1 text-xs">
                                <Eye className="h-3 w-3" /> Viewed
                              </Badge>
                            ) : sig.status === 'declined' ? (
                              <Badge variant="destructive" className="gap-1 text-xs">
                                <XCircle className="h-3 w-3" /> Declined
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="text-blue-600 border-blue-300 gap-1 text-xs">
                                <Send className="h-3 w-3" /> Sent
                              </Badge>
                            )}
                            {sig.completed_at && (
                              <span className="text-[10px] text-muted-foreground">
                                {new Date(sig.completed_at).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                    {!selected.signatures?.length && Number(selected.signature_total) > 0 && (
                      <p className="text-xs text-muted-foreground text-center py-2">
                        {selected.signature_signed}/{selected.signature_total} signed — open detail to load full list
                      </p>
                    )}
                  </div>
                )}

                {/* Send for Signature (if has docs but no signatures yet) */}
                {selected.contact_email && !selected.signatures?.length && Number(selected.signature_total) === 0 && (
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1 gap-2"
                      onClick={async () => {
                        try {
                          await sendEngagementForSignature(selected.id);
                          const res = await api.get(`/admin/engagements/engagements/${selected.id}`);
                          const body = unwrap(res);
                          setSelected(body.data || body);
                          load();
                        } catch (err) { console.error('Send for signature failed', err); }
                      }}
                    >
                      <Pen className="h-4 w-4" /> Send for E-Signature
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-1"
                      onClick={() => { setShowSchedule('sig'); setScheduleDate(''); }}
                    >
                      <Clock className="h-4 w-4" /> Schedule
                    </Button>
                  </div>
                )}

                {/* Payment Link */}
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Payment Link</p>
                  <div className="flex items-center gap-2">
                    <Input
                      readOnly
                      value={`${window.location.origin}/pay/${selected.link_token}`}
                      className="font-mono text-xs"
                    />
                    <Button variant="outline" size="sm" onClick={() => copyPaymentLink(selected)} className="gap-1 shrink-0">
                      <Copy className="h-3.5 w-3.5" />
                      {linkCopied ? 'Copied!' : 'Copy'}
                    </Button>
                  </div>
                </div>

                {selected.description && (
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Description</p>
                    <p className="text-sm text-[#242424]">{selected.description}</p>
                  </div>
                )}
              </div>
              <DialogFooter className="flex-col sm:flex-row gap-2">
                {selected.status === 'active' && (
                  <>
                    <Button variant="outline" size="sm" onClick={() => handleStatusChange(selected, 'paused')} className="gap-1">
                      <PauseCircle className="h-4 w-4" /> Pause
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => handleStatusChange(selected, 'cancelled')} className="gap-1 text-red-600 hover:text-red-700">
                      <XCircle className="h-4 w-4" /> Cancel
                    </Button>
                    <Button size="sm" onClick={() => handleSendLink(selected)} disabled={sending || !selected.contact_email} className="gap-1">
                      <Send className="h-4 w-4" /> {sending ? 'Sending...' : 'Send Link'}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => { setShowSchedule('link'); setScheduleDate(''); }} disabled={!selected.contact_email} className="gap-1">
                      <Clock className="h-4 w-4" /> Schedule
                    </Button>
                  </>
                )}
                {selected.status === 'paused' && (
                  <Button size="sm" onClick={() => handleStatusChange(selected, 'active')} className="gap-1">
                    <CheckCircle className="h-4 w-4" /> Resume
                  </Button>
                )}
                {selected.status === 'draft' && (
                  <Button size="sm" onClick={() => handleStatusChange(selected, 'active')} className="gap-1">
                    <CheckCircle className="h-4 w-4" /> Activate
                  </Button>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Schedule Dialog */}
      <Dialog open={!!showSchedule} onOpenChange={(o) => { if (!o) setShowSchedule(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>
              {showSchedule === 'link' ? 'Schedule Payment Link' : 'Schedule E-Signature'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <label className="text-sm font-medium block">Send Date & Time</label>
            <Input
              type="datetime-local"
              value={scheduleDate}
              onChange={(e) => setScheduleDate(e.target.value)}
              min={new Date().toISOString().slice(0, 16)}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSchedule(null)}>Cancel</Button>
            <Button onClick={handleScheduleSend} disabled={!scheduleDate || sending}>
              <Clock className="h-3 w-3 mr-1" /> Schedule
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Engagements;
